function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class StlProductComponent extends HTMLElement {
  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    if (this._onBodyClick) {
      document.removeEventListener('click', this._onBodyClick);
    }
    if (this._onClick) {
      this.removeEventListener('click', this._onClick);
    }
    this._stlInitialized = false;
  }

  init() {
    if (this._stlInitialized) return;
    this._stlInitialized = true;
    this._onBodyClick = this.onBodyClick.bind(this);
    this._onClick = this.onClick.bind(this);
    document.addEventListener('click', this._onBodyClick);
    this.addEventListener('click', this._onClick);
  }

  get unique() {
    return this.getAttribute('data-unique');
  }

  setDropdownOpen(isOpen) {
    const card = this.closest('.complete-your-look__card');
    if (card) card.classList.toggle('is-stl-dropdown-open', Boolean(isOpen));
  }

  onClick(e) {
    const unique = this.unique;
    if (!unique) return;

    const trigger = e.target.closest(`[data-stl-size-trigger="${unique}"]`);
    const option = e.target.closest('[data-stl-size-option]');
    const dropdown = this.querySelector(`[data-stl-size-dropdown="${unique}"]`);
    const input = this.querySelector(`[data-stl-variant-input="${unique}"]`);
    const addBtn = this.querySelector(`[data-stl-add-btn="${unique}"]`);
    const label = this.querySelector('[data-stl-size-label]');

    if (trigger) {
      e.preventDefault();
      e.stopPropagation();
      if (dropdown) dropdown.classList.toggle('is-active');
      trigger.classList.toggle('is-active');
      this.setDropdownOpen(dropdown?.classList.contains('is-active'));
    } else if (option) {
      e.preventDefault();
      e.stopPropagation();

      const variantId = option.dataset.variantId;
      const variantTitle = option.dataset.variantTitle;
      const variantAvailable = option.dataset.variantAvailable === 'true';

      if (input) input.value = variantId;
      if (label) label.textContent = variantTitle;
      if (addBtn) addBtn.disabled = !variantAvailable;

      const options = dropdown ? dropdown.querySelectorAll('[data-stl-size-option]') : [];
      options.forEach((opt) => opt.classList.remove('is-selected'));
      option.classList.add('is-selected');

      if (dropdown) dropdown.classList.remove('is-active');
      const activeTrigger = this.querySelector(`[data-stl-size-trigger="${unique}"]`);
      if (activeTrigger) activeTrigger.classList.remove('is-active');
      this.setDropdownOpen(false);
    }
  }

  onBodyClick(e) {
    const unique = this.unique;
    if (!unique) return;

    const trigger = this.querySelector(`[data-stl-size-trigger="${unique}"]`);
    const dropdown = this.querySelector(`[data-stl-size-dropdown="${unique}"]`);
    if (trigger && dropdown && !trigger.contains(e.target) && !dropdown.contains(e.target)) {
      dropdown.classList.remove('is-active');
      trigger.classList.remove('is-active');
      this.setDropdownOpen(false);
    }
  }
}

function upgradeStlProductComponents(root) {
  if (!root) return;

  root.querySelectorAll('stl-product-component').forEach((component) => {
    if (typeof component.init === 'function') {
      component.init();
      return;
    }

    const replacement = document.createElement('stl-product-component');
    [...component.attributes].forEach(({ name, value }) => {
      replacement.setAttribute(name, value);
    });
    replacement.innerHTML = component.innerHTML;
    component.replaceWith(replacement);
  });
}

if (!customElements.get('stl-product-component')) {
  customElements.define('stl-product-component', StlProductComponent);
}

const COMPLETE_YOUR_LOOK_MOBILE_BREAKPOINT = 550;

function bindCompleteYourLookScroller(module) {
  if (!module) return;

  const scroller = module.querySelector('.complete-your-look__products');
  const arrow = module.querySelector('.complete-your-look__scroll-arrow');
  if (!scroller || !arrow) return;

  const getCards = () => scroller.querySelectorAll('.complete-your-look__card');

  const getScrollBounds = () => {
    const maxScroll = Math.max(scroller.scrollWidth - scroller.clientWidth, 0);
    return { maxScroll };
  };

  const isAtEnd = () => {
    const { maxScroll } = getScrollBounds();
    return scroller.scrollLeft >= maxScroll - 4;
  };

  const updateArrow = () => {
    const cards = getCards();
    const isMobile = window.innerWidth < COMPLETE_YOUR_LOOK_MOBILE_BREAKPOINT;

    if (!isMobile || cards.length < 3) {
      arrow.hidden = true;
      arrow.classList.remove('is-back');
      return;
    }

    arrow.hidden = false;
    arrow.classList.toggle('is-back', isAtEnd());
  };

  if (!module.dataset.cylScrollerInit) {
    module.dataset.cylScrollerInit = 'true';

    arrow.addEventListener('click', () => {
      if (window.innerWidth >= COMPLETE_YOUR_LOOK_MOBILE_BREAKPOINT) return;

      const { maxScroll } = getScrollBounds();

      if (isAtEnd()) {
        scroller.scrollTo({ left: 0, behavior: 'smooth' });
        return;
      }

      scroller.scrollTo({ left: maxScroll, behavior: 'smooth' });
    });

    scroller.addEventListener('scroll', updateArrow, { passive: true });
    scroller.addEventListener('scrollend', updateArrow, { passive: true });
    window.addEventListener('resize', updateArrow);

    const cardObserver = new MutationObserver(updateArrow);
    cardObserver.observe(scroller, { childList: true });
    module._cylCardObserver = cardObserver;
  }

  module._cylUpdateScroller = updateArrow;
  updateArrow();
  requestAnimationFrame(updateArrow);
}

function isCompleteYourLookExperimentActive(root = document.documentElement) {
  return (
    root.classList.contains('ab-complete-the-look') ||
    root.classList.contains('shopify-design-mode')
  );
}

function initCompleteYourLookScrollers(root = document) {
  if (!isCompleteYourLookExperimentActive()) return;

  const modules =
    root instanceof Element && root.matches('.complete-your-look__module')
      ? [root]
      : [...root.querySelectorAll('.complete-your-look__module')];

  modules.forEach((module) => {
    if (module.closest('.complete-your-look--cart')) return;
    bindCompleteYourLookScroller(module);
  });
}

function getPdpRecsSectionId() {
  const marker = document.querySelector('[data-cyl-pdp-recs-section-id]');
  if (marker?.dataset.cylPdpRecsSectionId) return marker.dataset.cylPdpRecsSectionId;

  const sectionEl =
    marker?.closest("[id^='shopify-section-']") ||
    document.querySelector("[id*='complete_your_look_recs']");

  if (sectionEl?.id) return sectionEl.id.replace(/^shopify-section-/, '');

  return null;
}

function getCylRecsSectionId(context) {
  if (context === 'cart') return 'cart-drawer';
  return getPdpRecsSectionId();
}

function bootCompleteYourLook(root = document) {
  initCompleteYourLookScrollers(root);
  initCompleteYourLookFallbacks(root);
}

function observeCompleteYourLookAbClass() {
  const root = document.documentElement;
  let wasActive = isCompleteYourLookExperimentActive(root);

  const handleChange = () => {
    const isActive = isCompleteYourLookExperimentActive(root);
    if (isActive && !wasActive) bootCompleteYourLook();
    wasActive = isActive;
  };

  const observer = new MutationObserver(handleChange);
  observer.observe(root, { attributes: true, attributeFilter: ['class'] });
}

function initCompleteYourLookFallbacks(root = document) {
  const scope = root instanceof Element ? root : document;

  scope.querySelectorAll('.cart-upsell--stl').forEach((container) => {
    upgradeStlProductComponents(container);
  });

  if (!isCompleteYourLookExperimentActive()) return;

  scope.querySelectorAll('.complete-your-look').forEach((container) => {
    upgradeStlProductComponents(container);
  });

  let missingPdpSectionId = false;

  scope.querySelectorAll('.complete-your-look__fallback[data-product-id]').forEach((element) => {
    const context = element.dataset.cylContext || 'pdp';

    if (!element.dataset.sectionId) {
      const recsSectionId = getCylRecsSectionId(context);
      if (!recsSectionId) {
        if (context === 'pdp') missingPdpSectionId = true;
        return;
      }
      element.dataset.sectionId = recsSectionId;
    }

    const grid = element.closest('.complete-your-look')?.querySelector('.complete-your-look__products');
    const cardCount = grid?.querySelectorAll('.complete-your-look__card').length || 0;

    if (cardCount >= 3) {
      element.dataset.cylLoaded = 'true';
      return;
    }

    if (element.dataset.cylLoading || element.dataset.cylLoaded === 'true') return;

    element.initializeRecommendations(element.dataset.productId);
  });

  if (missingPdpSectionId) {
    const retryRoot = scope instanceof Element ? scope : document.documentElement;
    if (!retryRoot.dataset.cylSectionIdRetried) {
      retryRoot.dataset.cylSectionIdRetried = 'true';
      requestAnimationFrame(() => initCompleteYourLookFallbacks(root));
    }
  }
}

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    if (this.classList.contains('complete-your-look__fallback')) {
      if (!isCompleteYourLookExperimentActive()) return;
      if (!this.dataset.sectionId) return;
      if (this.dataset.cylLoading || this.dataset.cylLoaded === 'true') return;
      this.loadRecommendations(productId);
      return;
    }

    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    const isCompleteYourLook = this.classList.contains('complete-your-look__fallback');

    if (isCompleteYourLook) {
      if (this.dataset.cylLoading) return;
      this.dataset.cylLoading = 'true';
    }

    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations =
          html.querySelector('product-recommendations.complete-your-look__fallback') ||
          html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (isCompleteYourLook) {
          const excludeIds = this.dataset.excludeIds || '';
          const slotsNeeded = Number(this.dataset.slotsNeeded || 0);
          const grid = this.closest('.complete-your-look')?.querySelector('.complete-your-look__products');
          let kept = 0;

          this.querySelectorAll('complete-your-look-product').forEach((card) => {
            const token = `,${card.dataset.cylProductId},`;
            if (!grid || excludeIds.includes(token) || kept >= slotsNeeded) {
              card.remove();
              return;
            }
            kept += 1;
            grid.appendChild(card);
          });
          this.dataset.cylLoaded = 'true';
          delete this.dataset.cylLoading;
          upgradeStlProductComponents(grid);
          const module = grid?.closest('.complete-your-look__module');
          if (module?._cylUpdateScroller) {
            module._cylUpdateScroller();
            requestAnimationFrame(() => module._cylUpdateScroller());
          } else if (module && !grid.closest('.complete-your-look--cart')) {
            initCompleteYourLookScrollers(module);
          }
        } else if (
          !this.querySelector('slideshow-component') &&
          this.classList.contains('complementary-products') &&
          !this.querySelector('.complete-your-look__module')
        ) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }

        if (isCompleteYourLook) return;

        const swiper = new Swiper("[js-product-recommendations-slider]", {
            slidesPerView: 4.1,
            spaceBetween: 8,
            watchSlidesProgress: true,
            draggable: true,
            autoHeight: false,
            watchOverflow: true,
            threshold: 10,
            mousewheel: {
              forceToAxis: true
            },
            breakpoints: {
                0: {
                    slidesPerView: 1.035,
                    spaceBetween: 8,
                },
                600: {
                    slidesPerView: 1.6,
                },
                768: {
                    slidesPerView: 2.4,
                },
                1024: {
                    slidesPerView: 3,
                    spaceBetween: 8,
                },
                1280: {
                    slidesPerView: 4.1,
                }
            }
        });
        document.dispatchEvent(new CustomEvent("handleSwatchHover:init"));
        document.dispatchEvent(new CustomEvent("variant-options:init"));
      })
      .catch((e) => {
        console.error(e);
        if (isCompleteYourLook) {
          delete this.dataset.cylLoading;
        }
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

document.addEventListener('DOMContentLoaded', () => {
  bootCompleteYourLook();
  observeCompleteYourLookAbClass();
});

document.addEventListener('cart-drawer:rendered', () => {
  initCompleteYourLookFallbacks(document.querySelector('cart-drawer-items') || document);
});

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

function cartUpsellSlider() {
    new Swiper('[js-cart-upsell-slider]', {
      slidesPerView: 'auto',
      spaceBetween: 4,
      mousewheel: {
        forceToAxis: true,
        sensitivity: 1,
      }
    });
 }
 cartUpsellSlider()
 document.addEventListener('cart-upsell-slider:init', cartUpsellSlider)


function initLoadMoreButton() {
  const paginatePage = document.querySelector("[js-paginate-page]");
  const loadMoreBtn = document.querySelector("[js-load-more-btn]");
 
  if (!paginatePage || !loadMoreBtn) return;
 
  loadMoreBtn.addEventListener("click", function () {
    let nextUrl = paginatePage.getAttribute("data-next-url");
    if (!nextUrl) return;
 
    const spinner = loadMoreBtn.querySelector(".loading__spinner");
    if (spinner) spinner.classList.remove("hidden");
 
    loadMoreBtn.disabled = true;
 
    fetch(nextUrl)
      .then(response => response.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const newArticles = doc.querySelector(".product-grid");
 
        if (newArticles) {
          paginatePage.insertAdjacentHTML("beforeend", newArticles.innerHTML);
          document.dispatchEvent(new CustomEvent("handleSwatchHover:init"));
          document.dispatchEvent(new CustomEvent("variant-options:init"));
        }
 
        const newNextUrl = newArticles ? newArticles.getAttribute("data-next-url") : null;
        paginatePage.setAttribute("data-next-url", newNextUrl || "");
 
        if (spinner) spinner.classList.add("hidden");
        loadMoreBtn.disabled = false;
 
        if (!newNextUrl) {
          loadMoreBtn.style.display = "none";
        }
      })
      .catch(error => {
        if (spinner) spinner.classList.add("hidden");
        loadMoreBtn.disabled = false;
        console.error("Load More Error:", error);
      });
  });
}
initLoadMoreButton()
document.addEventListener("load-more-btn:init", initLoadMoreButton);

document.addEventListener('DOMContentLoaded', function () {
  const STORAGE_KEY = 'recently-viewed-products';
  const MAX_ITEMS = 10;
 
  const product = window.ProductData;
  if (!product) return;
 
  function getRecentlyViewed() {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  }
 
  function saveRecentlyViewed(products) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(products));
  }
 
  function addCurrentProduct() {
    let products = getRecentlyViewed();
    products = products.filter(p => p.handle !== product.handle);
    products.unshift(product);
    if (products.length > MAX_ITEMS) products.pop();
    saveRecentlyViewed(products);
  }
 
  function renderRecentlyViewed() {
    const container = document.getElementById('RecentlyViewedGrid');
    if (!container) return;
 
    const products = getRecentlyViewed().filter(p => p.handle !== product.handle);
 
    if (!products.length) {
      container.innerHTML = '<p>No recently viewed products yet.</p>';
      return;
    }
 
    const promises = products.slice(0, 8).map(p => {
      console.log(p.handle,'handle');
     
      return fetch(`/products/${p.handle}?view=card`)
        .then(response => response.text())
        .then(html => html);
    });
 
    Promise.all(promises).then(cards => {
      container.innerHTML = cards.join('');

      document.dispatchEvent(new CustomEvent("handleSwatchHover:init"));
      document.dispatchEvent(new CustomEvent("variant-options:init"));
    });
  }
 
  addCurrentProduct();
  renderRecentlyViewed();
});

const swiper = new Swiper("[js-recently-viewed-slider]", {
  slidesPerView: 4.1,
  spaceBetween: 8,
  watchSlidesProgress: true,
  draggable: true,
  autoHeight: false,
  watchOverflow: true,
  threshold: 10,
  mousewheel: {
  forceToAxis: true
  },
  breakpoints: {
      0: {
          slidesPerView: 1.035,
          spaceBetween: 8,
      },
      600: {
          slidesPerView: 1.6,
      },
      768: {
          slidesPerView: 2.4,
      },
      1024: {
          slidesPerView: 3,
          spaceBetween: 8,
      },
      1280: {
          slidesPerView: 4.1,
      }
  }
});

new Swiper(".featured-collection-slider", {
  slidesPerView: 4.2,
  spaceBetween: 8,
  watchSlidesProgress: true,
  draggable: true,
  autoHeight: false,
  watchOverflow: true,
  threshold: 10,
  mousewheel: {
  forceToAxis: true
  },
  breakpoints: {
      0: {
          slidesPerView: 1.035,
          spaceBetween: 8,
      },
      600: {
          slidesPerView: 1.6,
      },
      768: {
          slidesPerView: 2.4,
      },
      1024: {
          slidesPerView: 3,
          spaceBetween: 8,
      },
      1280: {
          slidesPerView: 4.2,
          spaceBetween: 8,
      }
  }
});


function initPredictiveSearchSlider() {
  document.querySelectorAll(".predictive_search_slider").forEach((sliderEl) => {
    if (sliderEl.closest('.custom-header-drawer')) return;
    new Swiper(sliderEl, {
      spaceBetween: 8,
      slidesPerView: 1.4,
      watchSlidesProgress: true,
      draggable: true,
      autoHeight: false,
      watchOverflow: true,
      threshold: 10,
      mousewheel: {
      forceToAxis: true
      },
      navigation: {
        nextEl: sliderEl.querySelector(".swiper-button-next"),
        prevEl: sliderEl.querySelector(".swiper-button-prev"),
      },
      breakpoints: {
        640: {
          slidesPerView: 2,
        },
        749: {
          slidesPerView: 3,
        },
        1260: {
          slidesPerView: 4,
        },
      },
    });
  });
}

initPredictiveSearchSlider();

document.addEventListener('predictive-search-slider:init', initPredictiveSearchSlider);

function handleSwatchHover() {
  const productCards = document.querySelectorAll('[js-product-card]');
  if (!productCards) return;

  productCards.forEach(function (card) {
    const swatchImages = card.querySelectorAll('[js-swatch-image]');
    const primaryImage = card.querySelector('[js-product-image]');
    const secondaryImage = card.querySelector('[js-secondary-image]');
    const swatchColors = card.querySelectorAll('[js-hb-swatch-color]');

    swatchImages.forEach(function (swatchImg, index) {

      function updateSwatch() {
        const priceCompareElement = card.querySelector("[js-compare-price]");
        const priceElement = card.querySelector("[js-price]");
        const salePriceElement = card.querySelector("[js-sale-price]");
        const currentProductHandle = swatchImg.href;
        const currentVariantPrice = swatchImg.getAttribute("data-variant-price");
        const currentVariantComparePrice = swatchImg.getAttribute("data-variant-compare-price");
        const productLinks = card.querySelectorAll('[js-product-variant-url]');
        const productModelHandles = card.querySelectorAll('[js-product-model-handle]');
        const relatedProductOptions = card.querySelectorAll('[js-related-product-options]');
        const productId = swatchImg.getAttribute("data-product-id");
        const productTitle = swatchImg.getAttribute("data-product-title");

        priceElement.textContent = currentVariantPrice;
        salePriceElement.textContent = currentVariantPrice;
        priceCompareElement.textContent = currentVariantComparePrice;

        productLinks.forEach(productLink => {
          productLink.href = currentProductHandle;
        });
        productModelHandles.forEach(productModelHandle => {
          productModelHandle.dataset.productUrl = currentProductHandle;
        });

        const titleElements = card.querySelectorAll('[js-product-title]');
        titleElements.forEach(titleEl => {
          if (productTitle) {
            titleEl.textContent = productTitle;
          }
        });

        swatchColors.forEach(color => color.classList.remove('selected'));

        swatchColors[index].classList.add('selected');

        const primaryImageUrl = swatchImg.getAttribute('data-primary-image-src');
        const secondaryImageUrl = swatchImg.getAttribute('data-secondary-image-src');

        if (primaryImageUrl) {
          primaryImage.setAttribute('srcset', primaryImageUrl);
          primaryImage.setAttribute('src', primaryImageUrl);
        }

        if (secondaryImageUrl) {
          secondaryImage?.setAttribute('srcset', secondaryImageUrl);
          secondaryImage?.setAttribute('src', secondaryImageUrl);
          card.classList.add("product-grid-item");
        } else {
          card.classList.remove("product-grid-item");
        }

        relatedProductOptions?.forEach(el => el.classList.add("hidden"));
        card.querySelector(`[js-related-product-options][data-product-options-id="${productId}"]`)?.classList.remove("hidden");
      }

      swatchImg.addEventListener('mouseenter', updateSwatch);

      swatchImg.addEventListener('click', function (e) {
        e.preventDefault();
        updateSwatch();
      });

      swatchImg.addEventListener('mouseleave', function () {
        swatchColors[index].classList.remove('selected');
      });
    });
  });
}

handleSwatchHover();
document.addEventListener('handleSwatchHover:init', handleSwatchHover);


document.addEventListener('DOMContentLoaded', () => {
  function initVariantOptions() {
      document.querySelectorAll('[js-product-option-modle]').forEach((modle) => {
        const option = modle.closest('div, section, article')?.querySelector('[js-product-option]');

        if (!option) {
          return;
        }
        modle.addEventListener('click', (e) => {
          e.stopPropagation();
          modle.classList.add('active');
          option.classList.add('active');
        });

        document.addEventListener('click', (e) => {
          if (!modle.contains(e.target) && !option.contains(e.target)) {
            modle.classList.remove('active');
            option.classList.remove('active');
          }
        });
    });
  };

  initVariantOptions();
  document.addEventListener("variant-options:init", initVariantOptions);
});
// Perf: lightweight quick-add — size buttons share one form per card.
// Set the hidden variant id from the clicked button before the form submits.
document.addEventListener('click', function (e) {
  var btn = e.target && e.target.closest ? e.target.closest('.product-option__button[data-variant-id]') : null;
  if (!btn) return;
  var form = btn.closest('form');
  if (!form) return;
  var idInput = form.querySelector('input[name="id"]');
  if (idInput) idInput.value = btn.getAttribute('data-variant-id');
});

// Perf/UX: adaptiv gallerihøjde på mobil.
// Slideren er en flex-række hvis højde ellers sættes af det højeste medie
// (fx en 9:16-video), så billeder får hvid luft over/under. Her følger
// højden i stedet det AKTIVE slides eget format, med blød overgang.
(function () {
  var MOBILE_MAX = 749;
  function init() {
    document.querySelectorAll('.product__media-wrapper .product__media-list').forEach(function (list) {
      var slides = Array.prototype.slice.call(list.querySelectorAll('.product__media-item'));
      if (slides.length < 2) return;

      function mediaOf(slide) { return slide.querySelector('.product-media-container'); }
      function setH(slide) {
        if (window.innerWidth > MOBILE_MAX) { list.style.height = ''; return; }
        var m = mediaOf(slide);
        if (!m) return;
        var h = m.offsetHeight;
        if (h > 40) list.style.height = h + 'px';
      }

      var active = slides[0];
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting && e.intersectionRatio >= 0.6) { active = e.target; setH(active); }
        });
      }, { root: list, threshold: 0.6 });
      slides.forEach(function (s) { io.observe(s); });

      // Første måling + når billeder lazy-loader færdig
      slides.forEach(function (s) {
        var img = s.querySelector('img');
        if (img && !img.complete) img.addEventListener('load', function () { if (s === active) setH(s); }, { once: true });
      });
      setH(active);
      window.addEventListener('resize', function () { setH(active); });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
